export type {
  IAppConfiguration,
  IServerAppConfiguration,
  IStaticAppConfiguration,
  IStaticServerAppConfiguration,
} from "./configuration-types.js";
import type {
  IAppConfiguration,
  IServerAppConfiguration,
  IStaticAppConfiguration,
  IStaticServerAppConfiguration,
} from "./configuration-types.js";

export const appConfigurationSecretEnvironmentVariables = {
  avuxiKey: "APP_CONFIG_AVUXI_KEY",
  unleashFrontendToken: "APP_CONFIG_UNLEASH_FRONTEND_TOKEN",
  googleMapsKey: "APP_CONFIG_GOOGLE_MAPS_KEY",
  recaptchaKey: "APP_CONFIG_RECAPTCHA_KEY",
  mixpanelToken: "APP_CONFIG_MIXPANEL_TOKEN",
  trustpilotBadgeDataToken: "APP_CONFIG_TRUSTPILOT_BADGE_DATA_TOKEN",
  trustpilotLoaderDataToken: "APP_CONFIG_TRUSTPILOT_LOADER_DATA_TOKEN",
} as const;

const getEnvironmentValue = (
  environmentVariables: Readonly<Record<string, string | undefined>>,
  key: string,
): string | undefined => {
  const value = environmentVariables[key];
  return typeof value === "string" ? value : undefined;
};

export const resolveAppConfigurationRuntimeKeysAndTokens = (
  configuration: Readonly<IStaticAppConfiguration>,
  environmentVariables: Readonly<Record<string, string | undefined>>,
): IAppConfiguration => {
  const avuxiKey =
    getEnvironmentValue(environmentVariables, appConfigurationSecretEnvironmentVariables.avuxiKey) ??
    configuration.avuxi?.key ??
    "";
  const unleashFrontendToken =
    getEnvironmentValue(environmentVariables, appConfigurationSecretEnvironmentVariables.unleashFrontendToken) ??
    configuration.unleash.frontend.token ??
    "";
  const googleMapsKey =
    getEnvironmentValue(environmentVariables, appConfigurationSecretEnvironmentVariables.googleMapsKey) ??
    configuration.googleMaps.key ??
    "";
  const recaptchaKey =
    getEnvironmentValue(environmentVariables, appConfigurationSecretEnvironmentVariables.recaptchaKey) ??
    configuration.recaptchaKey ??
    null;
  const mixpanelToken =
    getEnvironmentValue(environmentVariables, appConfigurationSecretEnvironmentVariables.mixpanelToken) ??
    configuration.mixpanel.token ??
    "";
  const trustpilotBadgeDataToken =
    getEnvironmentValue(environmentVariables, appConfigurationSecretEnvironmentVariables.trustpilotBadgeDataToken) ??
    configuration.trustpilot?.badgeDataToken ??
    "";
  const trustpilotLoaderDataToken =
    getEnvironmentValue(environmentVariables, appConfigurationSecretEnvironmentVariables.trustpilotLoaderDataToken) ??
    configuration.trustpilot?.loaderDataToken ??
    "";

  return {
    ...configuration,
    avuxi: {
      ...(configuration.avuxi ?? {}),
      key: avuxiKey,
    },
    unleash: {
      frontend: {
        ...configuration.unleash.frontend,
        token: unleashFrontendToken,
      },
      environment: configuration.unleash.environment,
    },
    googleMaps: {
      ...configuration.googleMaps,
      key: googleMapsKey,
    },
    recaptchaKey,
    mixpanel: {
      ...configuration.mixpanel,
      token: mixpanelToken,
    },
    trustpilot: {
      ...(configuration.trustpilot ?? {}),
      badgeDataToken: trustpilotBadgeDataToken,
      loaderDataToken: trustpilotLoaderDataToken,
    },
  };
};

export const resolveServerAppConfiguration = (
  configuration: Readonly<IStaticServerAppConfiguration>,
): IServerAppConfiguration => {
  return { ...configuration };
};

let appConfiguration: Readonly<IAppConfiguration> | undefined;
let serverAppConfiguration: Readonly<IServerAppConfiguration> | undefined;

export const initializeConfiguration = (configuration: Readonly<IAppConfiguration>): void => {
  appConfiguration = configuration;
};

export const initializeServerConfiguration = (configuration: Readonly<IServerAppConfiguration>): void => {
  serverAppConfiguration = configuration;
};

const validateAppConfiguration = (appConfigurationToValidate: Readonly<IAppConfiguration> | undefined) => {
  if (!appConfigurationToValidate) {
    throw new Error("App configuration is not initialized");
  }

  if (!appConfigurationToValidate.riskified.domain) {
    throw new Error("Riskified domain is not configured");
  }
};

const validateServerAppConfiguration = (
  serverAppConfigurationToValidate: Readonly<IServerAppConfiguration> | undefined,
) => {
  if (!serverAppConfigurationToValidate) {
    throw new Error("Server app configuration is not initialized");
  }
};

export const getAppConfiguration = (): Readonly<IAppConfiguration> => {
  validateAppConfiguration(appConfiguration);
  return appConfiguration!;
};

export const getServerAppConfiguration = (): Readonly<IServerAppConfiguration> => {
  validateServerAppConfiguration(serverAppConfiguration);
  return serverAppConfiguration!;
};
